import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  baselineCostsOptions,
  designResultOptions,
  networkDataOptions,
  networkModeFor,
  scenarioCostsOptions,
} from '@/core/cache/cardQueries'
import { useAppState, useNetworkDataState, useStatsSettingsState } from '@/core/state/react'
import { activeCards as activeCardsFor, buildCards, demandDisplayFor, type StatCard } from '@/features/summary-statistics/cards'
import { computeStats, costDelta, formatCostDelta, formatCostDeltaPercent, type StoreCosts } from '@/features/summary-statistics/stats'
import { useSummaryStatsSettings } from './useSummaryStatsSettings'
import { useUnitConversions } from './useUnitConversions'

/**
 * Everything the stat cards need, as one React hook (Phase 1I).
 *
 * It composes the four shared queries, the three Zustand stores and the pure
 * functions the Vue cards already run on — so the React cards cannot show
 * different numbers from the Vue ones, and this can be tested without rendering
 * anything.
 *
 * Who owns what, because this has been wrong before:
 *  - the SERVER owns which cards are shown (`activeCards`);
 *  - the STATS STORE owns presentation mode, comparison mode, the two
 *    calculation methods and the scenario a comparison is measured against;
 *  - the APP STORE's `baselineScenarioId` is a different concept — the
 *    workspace's own baseline — and is deliberately not read here.
 */
export function useStatCards() {
  const scenarioId = useAppState((s) => s.currentScenarioId)
  const scenarioName = useAppState((s) => s.currentScenario)
  const workspaceId = useAppState((s) => s.workspaceId || 0)
  const resultType = useAppState((s) => s.currentResultType || 'optimization')
  const globalDisplayUnit = useAppState((s) => s.globalDisplayUnit)

  const comparisonMode = useStatsSettingsState((s) => s.comparisonMode)
  const presentationMode = useStatsSettingsState((s) => s.presentationMode)
  const capacityCalculationMethod = useStatsSettingsState((s) => s.capacityCalculationMethod)
  const serviceLevelCalculationMethod = useStatsSettingsState((s) => s.serviceLevelCalculationMethod)
  const baselineScenarioId = useStatsSettingsState((s) => s.baselineScenarioId)

  // The network the map is drawing. Empty means "nothing published yet", and the
  // query below is the fallback — the same arrangement the Vue cards have.
  const storeFacilities = useNetworkDataState((s) => s.facilities)
  const storeCustomers = useNetworkDataState((s) => s.customers)

  const costs = useQuery(scenarioCostsOptions({ scenarioId, resultType, workspaceId }))
  const baseline = useQuery(baselineCostsOptions({ baselineScenarioId, workspaceId }))
  const design = useQuery(designResultOptions({ scenarioId, workspaceId }))
  const network = useQuery({
    ...networkDataOptions({ scenarioId, mode: networkModeFor(scenarioName), workspaceId }),
    // Only fetched when the map has published nothing.
    enabled: storeFacilities.length === 0 && scenarioId != null && scenarioId > 0,
  })

  const { settings, saveSettings } = useSummaryStatsSettings(scenarioId, workspaceId)
  const { baseUnit, availableUnits, convert } = useUnitConversions(scenarioId, workspaceId)

  /**
   * Did the cost request FAIL with nothing to fall back on?
   *
   * Distinct from "loading" and from "zero": `$0` is a measurement saying this
   * network costs nothing, and a failed request must not be able to say it.
   */
  const costsUnavailable = Boolean(costs.error) && !costs.data

  const storeCosts: StoreCosts = useMemo(() => {
    if (costsUnavailable) {
      return { total_cost: null, fixed_cost: null, variable_cost: null, shipping_cost: null, holding_cost: null }
    }
    const data = (costs.data || {}) as Record<string, number | undefined>
    return {
      total_cost: data.total_cost || 0,
      fixed_cost: data.fixed_cost || 0,
      variable_cost: data.variable_cost || 0,
      shipping_cost: data.shipping_cost || 0,
      holding_cost: data.holding_cost || 0,
    }
  }, [costs.data, costsUnavailable])

  /**
   * The network the cards measure: the map's rows when it has published any,
   * otherwise this hook's own query.
   *
   * Memoized, because `[]` and a fresh `.facilities` are new objects on every
   * render, and everything below depends on these.
   */
  const facilities = useMemo(
    () => (storeFacilities.length
      ? storeFacilities
      : ((network.data as { facilities?: unknown[] })?.facilities ?? [])),
    [storeFacilities, network.data],
  )
  const customers = useMemo(
    () => (storeCustomers.length
      ? storeCustomers
      : ((network.data as { customers?: unknown[] })?.customers ?? [])),
    [storeCustomers, network.data],
  )

  const stats = useMemo(() => {
    const costData = (costs.data || {}) as { total_demand?: number }
    return computeStats({
      optimizationResults: (costs.data ?? null) as never,
      storeCosts,
      facilities: facilities as never,
      customers: customers as never,
      baselineCosts: (baseline.data || {}) as never,
      baselineResults: (baseline.data ?? null) as never,
      capacityCalculationMethod,
      serviceLevelCalculationMethod,
      reportedTotalDemand: costData.total_demand,
    })
  }, [
    costs.data, storeCosts, facilities, customers, baseline.data,
    capacityCalculationMethod, serviceLevelCalculationMethod,
  ])

  /**
   * Total Demand's number AND the unit it is actually in — decided together,
   * because when there is no conversion path the number stays in the base unit
   * and so must the label.
   *
   * The unit the user picked: the global display unit wins, then the saved
   * setting, then the base unit the data is stored in.
   */
  const demandDisplay = useMemo(
    () => demandDisplayFor(
      stats.totalDemand,
      (globalDisplayUnit || settings.demandUOM || baseUnit) ?? null,
      baseUnit,
      convert,
    ),
    [stats.totalDemand, globalDisplayUnit, settings.demandUOM, baseUnit, convert],
  )

  const cards: StatCard[] = useMemo(
    () => activeCardsFor(
      buildCards({
        stats,
        storeCosts,
        designResult: (design.data ?? null) as never,
        demandDisplay,
        capacityCalculationMethod,
        serviceLevelCalculationMethod,
      }),
      settings.activeCards,
    ),
    [stats, storeCosts, design.data, demandDisplay, capacityCalculationMethod, serviceLevelCalculationMethod, settings.activeCards],
  )

  /**
   * What the detail dialog's charts read. Memoized because it is an effect
   * dependency there: a new object each render would rebuild the chart on every
   * render and Chart.js would refuse the second bind.
   */
  const chartData = useMemo(
    () => ({
      utilizationPct: stats.utilizationPct,
      serviceLevelPct: stats.serviceLevel,
      demandFulfilledPct: stats.demandFulfilled,
      effectiveFacilities: facilities,
    }),
    [stats.utilizationPct, stats.serviceLevel, stats.demandFulfilled, facilities],
  )

  const delta = costDelta(storeCosts.total_cost, (baseline.data as { total_cost?: number })?.total_cost)
  // Nothing to compare when the chosen baseline IS the scenario on screen: both
  // queries then key the same cache entry and the card would report a confident
  // 0% difference from itself.
  const comparisonAvailable = Number(baselineScenarioId) !== Number(scenarioId)

  return {
    cards,
    chartData,
    demandDisplay,
    presentationMode,
    comparisonMode,
    showDemandUOM: settings.showDemandUOM !== false,
    /**
     * How the currency cards are written. Saved by the Customize Map modal's
     * radio, and until Phase 1I-followup read by nothing that renders the bar.
     */
    numberFormat: settings.numberFormat,
    baseUnit,
    availableUnits,

    isLoading: costs.isLoading,
    costsUnavailable,
    retryCosts: () => costs.refetch(),

    /**
     * The card row's ✕. It writes to the SERVER's settings, which is what
     * decides the card set — the Zustand copy the old emit updated was read by
     * nothing.
     */
    hideCard: (cardId: string) =>
      saveSettings({ activeCards: { ...settings.activeCards, [cardId]: false } }),

    comparison: {
      available: comparisonAvailable,
      delta: comparisonAvailable ? delta : null,
      formattedDelta: comparisonAvailable ? formatCostDelta(delta) : null,
      formattedPercent: comparisonAvailable ? formatCostDeltaPercent(delta) : null,
      // Comparison is on but there is nothing to say; silence reads as a broken
      // switch, so the card says which of the two reasons applies.
      note: comparisonAvailable ? 'Baseline has no results yet' : 'Baseline is this scenario',
    },
  }
}
